import { Accessor, For, Match, Show, Switch, createContext, createEffect, createMemo, createRoot, createSignal } from 'solid-js'
import { createSharedStore, debounce, requireContext } from 'common'
import { StudentSelectContext } from "./StudentSelectContext"
import { Image, Flexbox, requireDataStoreContext, requireFocusContext, requireViewContext, Dropdown, Button, Combobox, Icon, useViewContext, DataStore, DataStoreContext, Loader, GridLayout } from 'st-app'
import { LookupValuePair, ViewState } from 'st-app/api'
import { StudentEntityHistory, StudentEntityRecord, studentSelect as api } from 'sis-app/api'

import styles from './sis-student-select.module.css'
import { SetStoreFunction } from 'solid-js/store'

let studentEntityHistoryStore: StudentEntityHistory;
let setStudentEntityHistory: SetStoreFunction<StudentEntityHistory>;

createRoot(() => {
    [studentEntityHistoryStore, setStudentEntityHistory] = createSharedStore<StudentEntityHistory>('sis.student-history', { students: [], views: [] });
})

let loadingStudentHistory = false;
export function studentEntityHistory() {
    if (studentEntityHistoryStore.students.length === 0 && !loadingStudentHistory) {
        loadingStudentHistory = true;
        api.GetUserHistory().then(res => {
            if (res.students.length > 0) {
                setStudentEntityHistory(res);
            }
            loadingStudentHistory = false;
        });
    }
    return studentEntityHistoryStore;
}
export { setStudentEntityHistory };

const StudentSelectContextJsx = createContext<StudentSelectContext>();
export const requireStudentSelectContext = () => {
    return requireContext(StudentSelectContextJsx);
}

export class StudentSelectProps {
    identity?: string | undefined = '';
    suppressDropdown?: boolean = false;
}

export default function StudentSelect(props: StudentSelectProps) {
    const context = new StudentSelectContext();

    return (
        <StudentSelectContextJsx.Provider value={context}>
            <StudentSelectInternal {...props} />
        </StudentSelectContextJsx.Provider>
    )
}

function StudentSelectInternal(props: StudentSelectProps) {
    const viewContext = useViewContext();
    const dataStore = requireDataStoreContext();
    const [isLoading, setIsLoading] = createSignal(false);

    const [search, setSearch] = createSignal('');
    const [value, setValue] = createSignal<LookupValuePair>();
    const [searchResults, setSearchResults] = createSignal<LookupValuePair[]>([]);
    const [showDropdown, setShowDropdown] = createSignal(false);

    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName', value()?.value));

    const find = debounce(async (s: string) => {
        if (!s) {
            setSearchResults([]);
        }
        else {
            setIsLoading(true);
            try {
                const viewState = viewContext?.viewState();
                const findResults = await api.Find(viewState ?? new ViewState(), s);

                for (const stu of findResults) {
                    dataStore.setCurrentValue('K12-Student-Photo', stu.photo, stu.studentGU);
                    dataStore.setCurrentValue('K12-Student-SisNumber', stu.sisNumber, stu.studentGU);
                    dataStore.setCurrentValue('K12-Student-JoinByStudentGU-FormattedName', stu.formattedName, stu.studentGU);
                    dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-Grade', stu.grade, stu.studentGU);
                    dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-GradeDesc', stu.gradeDesc, stu.studentGU);
                    dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-StudentSchoolYearGU', stu.studentSchoolYearGU, stu.studentGU);
                    dataStore.setCurrentValue('K12-Student-FocusKey', stu.focusKey, stu.studentGU);
                }

                setSearchResults(findResults.map((stu) => ({
                    value: stu.studentGU,
                    displayValue: stu.formattedName
                })));
            }
            finally {
                setIsLoading(false);
            }
        }
    }, 500);

    const [contRef, setContRef] = createSignal<HTMLDivElement>();
    createEffect(() => find(search()));
    createEffect(() => {
        const val = value();

        if (val) {
            setShowDropdown(false);
            dataStore.setCurrentValue('K12-Student-StudentGU', val.value);
        }
    });

    function clearValue() {
        setValue(undefined);
        setShowDropdown(false);
    }

    function launch() {
        if (!value()) {
            setShowDropdown(true);
        }
    }

    return <>
        <Flexbox dir="vertical">
            <Flexbox middle dir="horizontal" class={styles.input} ref={setContRef} onClick={launch} style="padding: 2px 6px" >
                <Flexbox.Item as="button" aria-label="Select a Student" auto class={`${styles.value} ${styles.btn}`} >
                    {stuFormattedName()}
                </Flexbox.Item>
                <Show when={!props.suppressDropdown}>
                    <Flexbox.Item class={styles.addon} >
                        <Switch fallback={<Loader size="small" />}>
                            <Match when={!isLoading()}>
                                <Button type='link' label="Edit" style="padding: 0px" onClick={() => setShowDropdown(true)} >
                                    <Icon src="fas fa-link" />
                                </Button>
                            </Match>
                        </Switch>
                    </Flexbox.Item>
                </Show>
            </Flexbox>
            <StudentFKValue />

            <Show when={studentEntityHistory().students.length > 0}>
                <Flexbox.Item style="background-color: var(--st-panel-bg-nested); margin-top: 15px; padding: 5px">
                    <h3 style="margin: 5px 0; opacity: 0.8; font-weight: bold">Recent Students</h3>
                    <GridLayout columns={3} style="padding: 5px">
                        <For each={studentEntityHistory().students.slice(0, 6)}>{stu =>
                            <RecentStudent stu={stu} setValue={setValue} />
                        }</For>
                    </GridLayout>
                </Flexbox.Item>
            </Show>
        </Flexbox>

        <Dropdown open={[showDropdown, setShowDropdown]} placement='bottom-start' target={contRef()} >
            <Dropdown.Content>
                <Combobox value={[value, setValue]} filter={[search, setSearch]} filterIcon="fas fa-search" items={searchResults()} fallback={<Flexbox.Item><i>No Results</i></Flexbox.Item>} >{it =>
                    <StudentFKRow size={32} identity={it.value} />
                }</Combobox>
                <Flexbox dir="horizontal">
                    <Flexbox.Item auto></Flexbox.Item>
                    <Flexbox.Item>
                        <Button type="link" onClick={clearValue}>clear</Button>
                    </Flexbox.Item>
                </Flexbox>
            </Dropdown.Content>
        </Dropdown>
    </>
}

function RecentStudent(props: { stu: StudentEntityRecord, setValue: (val: LookupValuePair | undefined) => void }) {
    const dataStore = requireDataStoreContext();

    function selectStudent(ev: Event) {
        const stu = props.stu;

        ev.preventDefault();
        dataStore.setCurrentValue('K12-Student-Photo', stu.photo, stu.studentGU);
        dataStore.setCurrentValue('K12-Student-SisNumber', stu.sisNumber, stu.studentGU);
        dataStore.setCurrentValue('K12-Student-JoinByStudentGU-FormattedName', stu.formattedName, stu.studentGU);
        dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-Grade', stu.grade, stu.studentGU);
        dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-GradeDesc', stu.gradeDesc, stu.studentGU);
        dataStore.setCurrentValue('K12-EnrollmentInfo-StudentSOREnrollment-StudentSchoolYearGU', stu.studentSchoolYearGU, stu.studentGU);

        props.setValue({ value: stu.studentGU, description: stu.formattedName });
    }

    return <Flexbox auto as="a" href="#" dir="horizontal" class={styles.student} onClick={selectStudent} padded >
        <Flexbox.Item>
            <Image useDefaultPhoto src={props.stu.photo} alt="Student Photo" width="24px" style="max-height: 32px" />
        </Flexbox.Item>
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {props.stu.formattedName}
            </Flexbox.Item>
            <Flexbox dir="vertical" padded style={{ 'font-size': '0.9em' }} >
                <Flexbox.Item style="font-size: 0.8em">{props.stu.sisNumber}</Flexbox.Item>
                <Show when={props.stu.lastAccessInfo?.focusOrganizationName}>
                    <Flexbox.Item style="font-size: 0.8em; opacity: 0.75">
                        {props.stu.lastAccessInfo.focusOrganizationName}
                    </Flexbox.Item>
                </Show>
            </Flexbox>
        </Flexbox>
    </Flexbox>
}

function StudentFKValue() {
    const dataStore = requireDataStoreContext();
    const viewCtx = useViewContext();

    const studentGU = createMemo(() => dataStore.get('currentValue', 'K12-Student-StudentGU'));
    const photoSrc = createMemo(() => dataStore.get('currentValue', 'K12-Student-Photo', studentGU()));
    const sisNumber = createMemo(() => dataStore.get('currentValue', 'K12-Student-SisNumber', studentGU()));
    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName', studentGU()));
    const gradeLevel = createMemo(() => dataStore.get('currentValue', 'K12-EnrollmentInfo-StudentSOREnrollment-GradeDesc', studentGU()));

    return <Flexbox dir="horizontal" class={styles['student-info']} >
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {stuFormattedName()}
            </Flexbox.Item>
            <Flexbox dir="vertical" padded style={{ 'font-size': '0.9em' }} >
                <Flexbox.Item>Sis Number: <strong>{sisNumber()}</strong></Flexbox.Item>
                <Flexbox.Item>Grade: <strong>{gradeLevel()}</strong></Flexbox.Item>
            </Flexbox>
        </Flexbox>
        <Flexbox.Item>
            <Image useDefaultPhoto src={photoSrc()} alt="Student Photo" width="80px" style="max-height: 150px" />
        </Flexbox.Item>
    </Flexbox>
}

function StudentFKRow(props: { identity?: string, size?: number }) {
    //const focus = requireFocusContext();
    const dataStore = requireDataStoreContext();
    const viewCtx = useViewContext();

    const photoSrc = createMemo(() => dataStore.get('currentValue', 'K12-Student-Photo', props.identity));
    const sisNumber = createMemo(() => dataStore.get('currentValue', 'K12-Student-SisNumber', props.identity));
    const stuFormattedName = createMemo(() => dataStore.get('currentValue', 'K12-Student-JoinByStudentGU-FormattedName', props.identity));
    const gradeLevel = createMemo(() => dataStore.get('currentValue', 'K12-EnrollmentInfo-StudentSOREnrollment-GradeDesc', props.identity));

    return <Flexbox dir="horizontal">
        <Flexbox auto dir="vertical">
            <Flexbox.Item style={{ "font-weight": 'bold', 'font-size': '1.2em' }} >
                {stuFormattedName()}
            </Flexbox.Item>
            <Flexbox dir="horizontal" padded style={{ 'color': 'var(--st-disabled-fg)', 'font-size': '0.9em' }} >
                <Flexbox.Item>{sisNumber()}</Flexbox.Item>
                <Flexbox.Item>{gradeLevel()}</Flexbox.Item>
            </Flexbox>
        </Flexbox>
        <Flexbox.Item>
            <Image useDefaultPhoto src={photoSrc()} alt="Student Photo" width="30" style="max-height: 150px" />
        </Flexbox.Item>
    </Flexbox>
}

StudentSelect.defaultProps = new StudentSelectProps();
